import { useCallback, useContext, useEffect, useMemo, useRef, useState, memo } from "react";
import { flushSync } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import useWindowSize from "../../utils/useWindowSize";
import { DataContainerContext } from "../../context/DataContainerContext";
import DataContainer from "../../services/DataContainer";
import { Loader } from "../../components/Loader/Loader";
import { MainSettings, MainTextsSettings } from "../../enum/enums";
import { Typography } from "../../components/typography";
import { TruckImageSequence, preloadImageSetAsync, evictAllImageSets, evictImageSet } from "../../components/TruckImageSequence/TruckImageSequence";
import Spinner from "../../components/LoadingSpinner";
import { InfoShield } from "../../components/InfoShield/InfoShield";
import { TruckSelectorHeader, PowerType } from "./TruckSelectorHeader";
import { TruckInfoPanel } from "./TruckInfoPanel";
import { Page } from "../../components/Page/Page";

const POWER_TYPES: PowerType[] = ["Electric", "Diesel", "Gaspowered"];

const raf = () => new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

const FRAME_POSITIONS = {
  PREVIOUS: 33,
  CURRENT: 19,
  NEXT: 6,
  FUTURE_NEXT_START: 0,
  FUTURE_NEXT_END: 6,
  PREVIOUS_OUT: 39,
};

interface LayoutConfig {
  truckW: string;
  spacing: number;
  marginTop: string;
  minWidth: number;
}

function powerTypeFromPathSegment(segment: string | undefined): PowerType | null {
  if (!segment) return null;
  const s = segment.toLowerCase();
  if (s === "electric") return "Electric";
  if (s === "diesel") return "Diesel";
  if (s === "gaspowered") return "Gaspowered";
  return null;
}

function buildInitialFrameTargets(products: any[], currentIndex: number): Record<number, number> {
  const n = products.length;
  if (n === 0) return {};
  if (n === 1) return { [products[0].id]: FRAME_POSITIONS.CURRENT };
  if (n === 2) {
    return {
      [products[0].id]: currentIndex === 0 ? FRAME_POSITIONS.CURRENT : FRAME_POSITIONS.PREVIOUS,
      [products[1].id]: currentIndex === 0 ? FRAME_POSITIONS.NEXT : FRAME_POSITIONS.CURRENT,
    };
  }
  return {
    [products[(currentIndex - 1 + n) % n].id]: FRAME_POSITIONS.PREVIOUS,
    [products[currentIndex].id]: FRAME_POSITIONS.CURRENT,
    [products[(currentIndex + 1) % n].id]: FRAME_POSITIONS.NEXT,
  };
}

export const TruckSelector = () => {
  const { 
    data, 
    currentLocale, 
    setActiveComponentIndex, 
    setProductDataLoaded 
  } = useContext(DataContainerContext);
  const navigate = useNavigate();
  const { locale: localeParam, powerCategory, truckSlug } = useParams<{
    locale: string;
    powerCategory?: string;
    truckSlug?: string;
  }>();
  const [trucks, setTrucks] = useState<any[]>([]);
  const [currentTruckIndex, setCurrentTruckIndex] = useState(0);
  const [selectedPowerType, setSelectedPowerType] = useState<PowerType>("Electric");
  const [isLoading, setIsLoading] = useState(true);
  const [frameTargets, setFrameTargets] = useState<Record<number, number>>({});
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSequencesLoading, setIsSequencesLoading] = useState(false);
  const [slideDirection, setSlideDirection] = useState<"next" | "prev" | null>(null);
  const [currentBackgroundImage, setCurrentBackgroundImage] = useState<string | null>(null);
  const [preloadedIds, setPreloadedIds] = useState<Set<number>>(new Set());
  const isDragging = useRef(false);
  const userOverrodePowerTabRef = useRef(false);
  const prevPathCarouselKeyRef = useRef<string | null>(null);

  const [hasInitialLoaded, setHasInitialLoaded] = useState(false);

  const { windowWidth, windowHeight, isMd, is2Md } = useWindowSize();

  const currentTruck = trucks[currentTruckIndex];
  const currentTruckName = currentTruck?.product_info?.description ?? "FH_Aero";

  const layoutConfig = useMemo<LayoutConfig>(() => {
  // Desktop — cap truck size so it always fits the carousel height.
  // The carousel area is roughly vh minus the top tab bar (~54px) and bottom section (~186px).
  // Spacing stays fixed at 42.5vw so truck centres keep their positions; the gap between
  // trucks simply grows when the window is shorter than fullscreen.   
    if (is2Md) {
      const desiredPx = windowWidth * 0.55;
      const carouselH = windowHeight - 240;
      const maxPx = carouselH * 1;
      const truckPx = Math.max(200, Math.min(desiredPx, maxPx));
      const truckVW = (truckPx / windowWidth) * 100;
      return { truckW: `${truckVW.toFixed(2)}vw`, spacing: 42.5, marginTop: "6vh", minWidth: 220 };
    }

    // Short screen (applies to tablet and mobile)
    if (windowHeight <= 650) return { truckW: "80vw", spacing: 48, marginTop: "4vh", minWidth: 0 };

    // Tablet / medium
    if (isMd) {
      const truckVW = 60;
      return { truckW: `${truckVW}vw`, spacing: Math.round(truckVW * 0.7), marginTop: "6vh", minWidth: 0 };
    }

    // Mobile
    const truckVW = Math.min(130, Math.max(95, 50700 / windowWidth));
    return { truckW: `${truckVW}vw`, spacing: Math.round(truckVW * 0.55), marginTop: "6vh", minWidth: 0 };
  }, [windowWidth, windowHeight, isMd, is2Md]);

  const pathPowerType = useMemo(() => powerTypeFromPathSegment(powerCategory), [powerCategory]);

  const segmentSettings = DataContainer.getSection(MainSettings.SEGMENT_SETTINGS)?.key_values;
  const mainSegments = DataContainer.getSection(MainSettings.MAIN_SEGMENTS)?.key_values;

  const segmentFilteredProducts = useMemo(() => {
    let products = data.filter((truck: any) => truck.ios_ar_assets?.length > 0);
    if (segmentSettings && mainSegments) {
      const segment = localStorage.getItem("segment");
      const segmentKey = Object.keys(mainSegments).find(key => key === segment);

      if (segmentKey) {
        const filteredProducts = segmentSettings[`${MainSettings.SHOW_PRODUCTS}${segmentKey}`];
        const productsIdsByOrder = filteredProducts && filteredProducts.replaceAll(" ", "").split(",");

        if (productsIdsByOrder && productsIdsByOrder.length > 0) {
          const segmentProducts = products.filter((prod: any) =>
            productsIdsByOrder.includes(prod.id.toString())
          );

          if (segmentProducts.length > 0) {
            const associativeIds: any = {};
            productsIdsByOrder.forEach((val: any, index: number) => {
              associativeIds[val] = index;
            });
            products = segmentProducts.sort((a: any, b: any) =>
              (associativeIds[a.id] - associativeIds[b.id])
            );
          }
        }
      }
    }
    return products;
  }, [data, segmentSettings, mainSegments]);

  const availablePowerTypes = useMemo(() => {
    const powerTypes: PowerType[] = [];
    segmentFilteredProducts.forEach((truck: any) => {
      const type = truck.category?.name as PowerType;
      if (POWER_TYPES.includes(type) && !powerTypes.includes(type)) {
        powerTypes.push(type);
      }
    });
    return powerTypes;
  }, [segmentFilteredProducts]);

  const truckTargetDirective = useMemo(() => {
    const slug = truckSlug?.trim();
    if (!slug || !pathPowerType || segmentFilteredProducts.length === 0) return null;
    const match = segmentFilteredProducts.find(
      (truck: any) =>
        truck.category.name === pathPowerType && truck.product_info?.description === slug
    );
    if (!match) return null;
    return { id: match.id, power: pathPowerType };
  }, [segmentFilteredProducts, truckSlug, pathPowerType]);

  useEffect(() => {
    if (powerCategory && !pathPowerType && localeParam) {
      navigate(`/${localeParam}`, { replace: true });
    }
  }, [powerCategory, pathPowerType, localeParam, navigate]);

  const pathCarouselKey = `${powerCategory ?? ""}|${truckSlug ?? ""}`;
  useEffect(() => {
    if (pathCarouselKey !== prevPathCarouselKeyRef.current) {
      prevPathCarouselKeyRef.current = pathCarouselKey;
      userOverrodePowerTabRef.current = false;
    }
  }, [pathCarouselKey]);

  useEffect(() => {
    if (availablePowerTypes.length === 0) return;

    if (userOverrodePowerTabRef.current) {
      if (!availablePowerTypes.includes(selectedPowerType)) {
        setSelectedPowerType(availablePowerTypes[0]);
      }
      return;
    }

    if (pathPowerType && availablePowerTypes.includes(pathPowerType)) return;

    if (selectedPowerType !== availablePowerTypes[0]) {
      setSelectedPowerType(availablePowerTypes[0]);
    }
  }, [availablePowerTypes, selectedPowerType, pathPowerType]);

  useEffect(() => {
    if (!data.length) return;

    const allowPathPower =
      !userOverrodePowerTabRef.current &&
      pathPowerType !== null &&
      availablePowerTypes.includes(pathPowerType);

    let power: PowerType = selectedPowerType;
    if (allowPathPower) {
      power = pathPowerType;
    }

    const products = segmentFilteredProducts.filter(
      (product: any) => product.category.name === power
    );

    let initialIndex = 0;
    if (truckTargetDirective !== null && truckTargetDirective.power === power) {
      const idx = products.findIndex((p: any) => p.id === truckTargetDirective.id);
      if (idx >= 0) initialIndex = idx;
    }

    if (power !== selectedPowerType) {
      setSelectedPowerType(power);
    }

    setCurrentTruckIndex(initialIndex);
    setTrucks(products);

    if (products.length) {
      setFrameTargets(buildInitialFrameTargets(products, initialIndex));
    }
    evictAllImageSets();
    setPreloadedIds(new Set());
    setHasInitialLoaded(false);
    setIsLoading(false);
  }, [
    data,
    segmentFilteredProducts,
    selectedPowerType,
    pathPowerType,
    truckTargetDirective,
    availablePowerTypes,
  ]);

  useEffect(() => {
    if (trucks.length > 0 && trucks[currentTruckIndex] && !isTransitioning) {
      setCurrentBackgroundImage(null);
      const backPlatesProduct = data.find((product: any) => product.id === 189);
      if (backPlatesProduct) {
        const backPlate = backPlatesProduct.component_groups?.[0]?.components.find(
          (component: any) => component.key === trucks[currentTruckIndex]?.product_info?.description
        );
        const backgroundImage = backPlate?.available_variations[0]?.assets[0]?.preview_image_url;
        if (backgroundImage) setCurrentBackgroundImage(backgroundImage);
      }
    }
  }, [data, currentTruckIndex, trucks, isTransitioning]);

  useEffect(() => {
    return () => {
      evictAllImageSets();
    };
  }, []);

  const handleConfigureTruck = useCallback(() => {
    if (isDragging.current) return;
    const currentTruck = trucks[currentTruckIndex];
    if (currentTruck) {
      if (DataContainer.mLoadedData) {
        if (DataContainer.getActualModelName() !== currentTruck.product_info.description.split("_")[0]) {
          DataContainer.resetData();
        }
      }
      setActiveComponentIndex(0);
      setProductDataLoaded(false);
      if(currentTruck.id >= 183 && currentTruck.id < 188) {
        navigate(`/${currentLocale?.locale}/configurator/${currentTruck.name.toLowerCase()}`);
      } else {
        navigate(`/${currentLocale?.locale}/configurator/${currentTruck.product_info.description}`);
      }
      DataContainer.logEvent("page_entered", "configurator", { product: "" });
    }
  }, [trucks, currentTruckIndex, currentLocale, navigate, setActiveComponentIndex, setProductDataLoaded]);

  const handleReadMore = useCallback(() => {
    window.open(
      DataContainer.sectionKey(
        "TruckSelector",
        `${currentTruckName}_readmore_url`
      )
    );
  }, [currentTruckName]);

  const frameFor = (truck: any, fallback: number) => {
    if (!truck) return fallback;
    const stored = frameTargets[truck.id];
    const frame = typeof stored === "number" ? stored : fallback;
    if (!is2Md) {
      const max = (truckImageMap.get(truck.id)?.length ?? 1) - 1;
      return Math.min(max, Math.round(frame / 2));
    }
    return frame;
  };

  const settleFrames = useCallback((newIndex: number) => {
    setFrameTargets(buildInitialFrameTargets(trucks, newIndex));
    setSlideDirection(null);
    setIsTransitioning(false);
  }, [trucks]);

  const goToNext = useCallback(async () => {
    if (isTransitioning || trucks.length < 2) return;

    const oldIndex = currentTruckIndex;
    if (trucks.length === 2 && oldIndex >= 1) return;

    const nextIndex = trucks.length === 2 ? oldIndex + 1 : (oldIndex + 1) % trucks.length;

    setCurrentBackgroundImage(null);

    const currentTruck = trucks[oldIndex];
    const prevTruckObj = trucks[(oldIndex - 1 + trucks.length) % trucks.length];
    const nextTruck    = trucks[nextIndex];
    const futureNextTruck = trucks[(nextIndex + 1) % trucks.length];

    if (trucks.length > 2) {
      if (trucks.length !== 3 || futureNextTruck.id !== prevTruckObj.id) {
        flushSync(() => {
          setFrameTargets(prev => ({ ...prev, [futureNextTruck.id]: FRAME_POSITIONS.FUTURE_NEXT_START }));
        });
      }
    }

    setSlideDirection("next");
    setIsTransitioning(true);

    await raf();

    const newFrames: Record<number, number> = {
      [currentTruck.id]: FRAME_POSITIONS.PREVIOUS,
      [nextTruck.id]:    FRAME_POSITIONS.CURRENT,
    };

    if (trucks.length > 2) {
      if (trucks.length === 3 && futureNextTruck.id === prevTruckObj.id) {
        newFrames[futureNextTruck.id] = FRAME_POSITIONS.FUTURE_NEXT_END;
      } else {
        newFrames[prevTruckObj.id]    = FRAME_POSITIONS.PREVIOUS_OUT;
        newFrames[futureNextTruck.id] = FRAME_POSITIONS.FUTURE_NEXT_END;
      }
    }

    setFrameTargets(prev => ({ ...prev, ...newFrames }));
    await wait(700);

    setCurrentTruckIndex(nextIndex);
    settleFrames(nextIndex);
  }, [isTransitioning, trucks, currentTruckIndex, settleFrames]);

  const goToPrevious = useCallback(async () => {
    if (isTransitioning || trucks.length < 2) return;

    const oldIndex = currentTruckIndex;
    if (trucks.length === 2 && oldIndex <= 0) return;

    const prevIndex = trucks.length === 2 ? oldIndex - 1 : (oldIndex - 1 + trucks.length) % trucks.length;

    setCurrentBackgroundImage(null);

    const currentTruck = trucks[oldIndex];
    const nextTruckObj = trucks[(oldIndex + 1) % trucks.length];
    const prevTruck    = trucks[prevIndex];
    const futurePrevTruck = trucks[(prevIndex - 1 + trucks.length) % trucks.length];

    if (trucks.length > 2) {
      if (trucks.length !== 3 || futurePrevTruck.id !== nextTruckObj.id) {
        flushSync(() => {
          setFrameTargets(prev => ({ ...prev, [futurePrevTruck.id]: FRAME_POSITIONS.PREVIOUS_OUT }));
        });
      }
    }

    setSlideDirection("prev");
    setIsTransitioning(true);

    await raf();

    const newFrames: Record<number, number> = {
      [prevTruck.id]:    FRAME_POSITIONS.CURRENT,
      [currentTruck.id]: FRAME_POSITIONS.NEXT,
    };

    if (trucks.length > 2) {
      if (trucks.length === 3 && futurePrevTruck.id === nextTruckObj.id) {
        newFrames[futurePrevTruck.id] = FRAME_POSITIONS.PREVIOUS;
      } else {
        newFrames[futurePrevTruck.id] = FRAME_POSITIONS.PREVIOUS;
        newFrames[nextTruckObj.id]    = FRAME_POSITIONS.FUTURE_NEXT_START;
      }
    }

    setFrameTargets(prev => ({ ...prev, ...newFrames }));
    await wait(700);

    setCurrentTruckIndex(prevIndex);
    settleFrames(prevIndex);
  }, [isTransitioning, trucks, currentTruckIndex, settleFrames]);

  const truckImageMap = useMemo(() => {
    const map = new Map<number, string[]>();
    trucks.forEach((truck: any) => {
      const urls = truck.ios_ar_assets?.map((a: any) => a.value) ?? [];
      map.set(truck.id, !is2Md ? urls.filter((_url: string, i: number) => i % 2 === 0) : urls);
    });
    return map;
  }, [trucks, is2Md]);

  // Mobile keeps current ±2 so the next swipe target is already loaded.
  // Desktop keeps ±1 for background loads (initial desktop preload is broader below).
  const visibleTrucks = useMemo(() => {
    if (!trucks.length) return [];
    const n = trucks.length;
    const radius = !is2Md ? 2 : 1;
    const indices = new Set<number>();
    for (let d = -radius; d <= radius; d++) {
      indices.add((currentTruckIndex + d + n) % n);
    }
    return Array.from(indices).map(i => trucks[i]);
  }, [trucks, currentTruckIndex, is2Md]);

  useEffect(() => {
    if (!trucks.length) return;
    let cancelled = false;

    const loadImages = async () => {
      if (!hasInitialLoaded && trucks.length > 0) {
        const n = trucks.length;
        const initialIndices = new Set<number>();

        if (!is2Md) {
          // Mobile: only current ± neighbors (±2)
          for (let d = -2; d <= 2; d++) {
            initialIndices.add((currentTruckIndex + d + n) % n);
          }
        } else {
          initialIndices.add(0); // first
          if (n > 1) initialIndices.add(1); // second
          if (n > 2) initialIndices.add(2); // third
          if (n > 3) initialIndices.add(n - 1); // last
          initialIndices.add(currentTruckIndex);
        }

        const initialTrucks = Array.from(initialIndices).map(idx => trucks[idx]).filter(Boolean);
        const currentTruckToLoad = trucks[currentTruckIndex];
        const firstTruckInArray = trucks[0];

        if (!preloadedIds.has(currentTruckToLoad?.id)) {
          setIsSequencesLoading(true);
        }

        // 1. Prioritize the CURRENT truck first
        if (currentTruckToLoad && !preloadedIds.has(currentTruckToLoad.id)) {
          await preloadImageSetAsync(truckImageMap.get(currentTruckToLoad.id) ?? [])
            .then(() => {
              if (!cancelled) {
                setPreloadedIds(prev => {
                  const next = new Set(prev).add(currentTruckToLoad.id);
                  setIsSequencesLoading(false);
                  return next;
                });
              }
            });
        } else if (currentTruckToLoad) {
          // If already preloaded, ensure loading state is cleared
          setIsSequencesLoading(false);
        }

        if (cancelled) return;

        // 2. Then load other initial trucks
        const otherInitialTrucks = initialTrucks.filter(t => t.id !== currentTruckToLoad?.id);

        const loadTruckImages = (truck: any) =>
          preloadImageSetAsync(truckImageMap.get(truck.id) ?? [])
            .then(() => {
              if (!cancelled) {
                setPreloadedIds(prev => {
                  const next = new Set(prev).add(truck.id);
                  if (truck.id === firstTruckInArray?.id) {
                    setIsSequencesLoading(false);
                  }
                  return next;
                });
              }
            });

        if (!is2Md) {
          for (const truck of otherInitialTrucks) {
            if (cancelled) return;
            await loadTruckImages(truck);
          }
        } else {
          await Promise.all(otherInitialTrucks.map(loadTruckImages));
        }

        if (cancelled) return;

        // 3. Desktop only: for remaining trucks, load starting and ending images
        if (is2Md) {
          const secondaryTrucks = trucks.filter(t => !initialIndices.has(trucks.indexOf(t)));
          await Promise.all(
            secondaryTrucks.map(truck => {
              const imgs = truckImageMap.get(truck.id) ?? [];
              if (imgs.length === 0) return Promise.resolve();
              return preloadImageSetAsync(imgs, [0, imgs.length - 1], true)
                .then(() => {
                  if (!cancelled) {
                    setPreloadedIds(prev => new Set(prev).add(truck.id));
                  }
                });
            })
          );
        }

        if (cancelled) return;
        setIsSequencesLoading(false);
        setHasInitialLoaded(true);
      } else if (hasInitialLoaded) {
        // Subsequent loads (background)
        const keepIds = new Set(visibleTrucks.map(t => t.id));

        // Mobile: free decoded frames for trucks that left the ±2 window
        if (!is2Md) {
          setPreloadedIds(prev => {
            const toEvict = Array.from(prev).filter(id => !keepIds.has(id));
            if (toEvict.length === 0) return prev;
            toEvict.forEach(id => evictImageSet(truckImageMap.get(id) ?? []));
            const next = new Set(prev);
            toEvict.forEach(id => next.delete(id));
            return next;
          });
        }

        const trucksToLoad = visibleTrucks.filter(t => !preloadedIds.has(t.id) && keepIds.has(t.id));

        if (trucksToLoad.length > 0) {
          // We don't set setIsSequencesLoading(true) here because we don't want to lock the carousel
          await Promise.all(
            trucksToLoad.map(truck =>
              preloadImageSetAsync(truckImageMap.get(truck.id) ?? [])
                .then(() => {
                  if (!cancelled) setPreloadedIds(prev => new Set(prev).add(truck.id));
                })
            )
          );
        }
      }
    };

    loadImages();
    return () => { cancelled = true; };
  }, [trucks, visibleTrucks, truckImageMap, hasInitialLoaded, is2Md]);

  let prevTruck: any = null;
  let nextTruck: any = null;

  if (trucks.length === 2) {
    if (currentTruckIndex === 0) nextTruck = trucks[1];
    else if (currentTruckIndex === 1) prevTruck = trucks[0];
  } else if (trucks.length > 2) {
    prevTruck = trucks[(currentTruckIndex - 1 + trucks.length) % trucks.length];
    nextTruck = trucks[(currentTruckIndex + 1) % trucks.length];
  }

  const futurePrevTruck = trucks.length > 2 ? trucks[(currentTruckIndex - 2 + trucks.length) % trucks.length] : null;
  const futureNextTruck = trucks.length > 2 ? trucks[(currentTruckIndex + 2) % trucks.length] : null;

  const slots = useMemo(() => [
    (futurePrevTruck && preloadedIds.has(futurePrevTruck.id)) ? {
      truck: futurePrevTruck, N: -2, slotKey: `fp-${futurePrevTruck.id}`,
      frameDur: isTransitioning && slideDirection === "prev" ? 700 : 0,
      frameTarget: FRAME_POSITIONS.PREVIOUS, zIndex: 1, clickable: false,
    } : null,
    prevTruck ? {
      truck: prevTruck, N: -1, slotKey: `p-${prevTruck.id}`,
      frameDur: isTransitioning ? 700 : 0,
      frameTarget: FRAME_POSITIONS.PREVIOUS, zIndex: 5, clickable: false,
    } : null,
    currentTruck ? {
      truck: currentTruck, N: 0, slotKey: `c-${currentTruck.id}`,
      frameDur: trucks.length === 1 ? 0 : (isTransitioning ? 700 : 0),
      frameTarget: FRAME_POSITIONS.CURRENT, zIndex: 10, clickable: true,
    } : null,
    nextTruck ? {
      truck: nextTruck, N: 1, slotKey: `n-${nextTruck.id}`,
      frameDur: isTransitioning ? 700 : 0,
      frameTarget: FRAME_POSITIONS.NEXT, zIndex: 5, clickable: false,
    } : null,
    (futureNextTruck && preloadedIds.has(futureNextTruck.id)) ? {
      truck: futureNextTruck, N: 2, slotKey: `fn-${futureNextTruck.id}`,
      frameDur: isTransitioning && slideDirection === "next" ? 700 : 0,
      frameTarget: FRAME_POSITIONS.FUTURE_NEXT_END, zIndex: 1, clickable: false,
    } : null,
  ].filter((s): s is NonNullable<typeof s> => s !== null),
  [futurePrevTruck, prevTruck, currentTruck, nextTruck, futureNextTruck, isTransitioning, slideDirection, trucks.length, preloadedIds]);

  return (
    <>
      {isLoading || !trucks.length ? (
        <Loader
          text={DataContainer.sectionKey(
            MainTextsSettings.MAIN_TEXTS,
            MainTextsSettings.LOADING_TEXT
          )}
        />
      ) : (
        <Page fullHeight className="overflow-hidden">
          <TruckSelectorHeader
            availablePowerTypes={availablePowerTypes}
            selectedPowerType={selectedPowerType}
            onPowerTypeSelect={(type) => {
              userOverrodePowerTabRef.current = true;
              setSelectedPowerType(type);
              if (powerCategory || truckSlug) {
                const loc = currentLocale?.locale ?? localeParam;
                if (loc) navigate(`/${loc}`, { replace: true });
              }
            }}
          />

          <div className="relative flex-grow w-full flex items-center justify-center overflow-hidden">
            <div className="absolute bottom-0 top-0 right-0 left-0 z-0">
              <AnimatePresence>
                {currentBackgroundImage && (
                  <motion.div
                    key={currentBackgroundImage}
                    className="absolute inset-0"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.7, ease: "easeInOut" }}
                  >
                    <img
                      src={currentBackgroundImage}
                      alt=""
                      className="w-full h-full object-cover xxl:object-fill"
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {isSequencesLoading && (
              <div className="absolute inset-0 z-20 flex flex-col gap-6 items-center justify-center pointer-events-none">
                <Spinner size={60} />
                <Typography
                  tag="h3"
                  className="flex text-base text-white text-center items-center self-center font-medium"
                >
                  {DataContainer.sectionKey(
                    "TruckSelector",
                    "loading_trucks_text"
                  )}
                </Typography>
              </div>
            )}

            <div className="relative z-10 w-full h-full overflow-hidden">
              <div className="absolute inset-0 flex items-center justify-center">
                {/*
                  Zero-size centered origin. Each truck is absolutely positioned from here
                  using: left = N * spacing − truckW/2  (centers the truck on its slot)
                         top  = −truckW/2 + marginTop   (centers vertically, then shifts down)
                  The origin itself animates ±spacing on navigation, moving all slots together.
                  After 700 ms the index updates, N values remap, and the strip resets to x:0
                  — the positions align perfectly so the snap is invisible.
                */}
                <motion.div
                  style={{ position: "relative", width: 0, height: 0 }}
                  animate={{
                    x:
                      slideDirection === "next"
                        ? `-${layoutConfig.spacing}vw`
                        : slideDirection === "prev"
                          ? `${layoutConfig.spacing}vw`
                          : "0vw",
                  }}
                  transition={{
                    duration: slideDirection ? 0.7 : 0,
                    ease: "easeInOut",
                  }}
                  initial={false}
                  drag={
                    !isTransitioning && !isSequencesLoading ? "x" : false
                  }
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.1}
                  onDragStart={() => {
                    isDragging.current = true;
                  }}
                  onDragEnd={(_, info) => {
                    // Reset the flag after the click event that fires in the same tick.
                    setTimeout(() => {
                      isDragging.current = false;
                    }, 0);
                    if (info.offset.x < -50) goToNext();
                    else if (info.offset.x > 50) goToPrevious();
                  }}
                >
                  {slots.map(
                    ({
                      truck,
                      N,
                      slotKey,
                      frameDur,
                      frameTarget,
                      zIndex,
                      clickable,
                    }) => (
                      <div
                        key={slotKey}
                        onClick={
                          clickable && !isSequencesLoading
                            ? handleConfigureTruck
                            : undefined
                        }
                        style={{
                          position: "absolute",
                          width: layoutConfig.truckW,
                          minWidth: layoutConfig.minWidth || undefined,
                          aspectRatio: "1 / 1",
                          left: `calc(${N} * ${layoutConfig.spacing}vw - ${layoutConfig.truckW} / 2)`,
                          top: `calc(${layoutConfig.truckW} / -2 + ${layoutConfig.marginTop} / 2)`,
                          zIndex,
                          cursor: clickable ? "pointer" : undefined,
                        }}
                      >
                        <TruckImageSequence
                          images={truckImageMap.get(truck.id) ?? []}
                          targetFrame={frameFor(truck, frameTarget)}
                          duration={frameDur}
                          className="w-full h-full"
                          alt={truck.product_info?.name || truck.name}
                        />
                      </div>
                    )
                  )}
                </motion.div>
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="flex gap-2 flex-col xxl:flex-row flex-shrink-0 basis-0 items-end self-stretch justify-between px-4 pb-4 xxl:px-20 xxl:pb-10 w-full">
            <div className="max-xxl:hidden flex flex-1 flex-col items-start">
              {DataContainer.sectionKey(
                "TruckSelector",
                `${currentTruckName}_campaign_url`
              ) !== "false" && (
                <button
                  onClick={() => {
                    window.open(
                      DataContainer.sectionKey(
                        "TruckSelector",
                        `${currentTruckName}_campaign_url`
                      )
                    );
                  }}
                  className="px-8 py-3.5 bg-[#2A609D] text-white hover:bg-[#4781c4] transition-colors text-sm font-medium rounded"
                >
                  {DataContainer.sectionKey(
                    "TruckSelector",
                    "viewcampaign_button_text"
                  )}
                </button>
              )}
            </div>

            <TruckInfoPanel
              currentTruckIndex={currentTruckIndex}
              totalTrucks={trucks.length}
              isSequencesLoading={isSequencesLoading}
              isTransitioning={isTransitioning}
              currentTruckDisplayName={
                currentTruck?.product_info?.name.split(" ").join(" ") ??
                currentTruck?.name.split(" ").join(" ") ?? ""
              }
              currentTruckName={currentTruckName}
              goToPrevious={goToPrevious}
              goToNext={goToNext}
            />

            <InfoShield />

            <div className="flex flex-col flex-1 justify-center xxl:justify-end items-end self-stretch gap-2">
              <div className="flex justify-end items-start w-full gap-2">
                <ActionButton
                  key="read-more-btn"
                  variant="outline"
                  onClick={handleReadMore}
                >
                  {DataContainer.sectionKey(
                    "TruckSelector",
                    "readmore_button_text"
                  )}
                </ActionButton>
                <ActionButton
                  key="configure-truck-btn"
                  variant="solid"
                  disabled={isSequencesLoading}
                  onClick={handleConfigureTruck}
                >
                  {DataContainer.sectionKey(
                    "TruckSelector",
                    "buildyourtruck_button_text"
                  )}
                </ActionButton>
              </div>
            </div>
          </div>
        </Page>
      )}
    </>
  );
};

type ActionButtonVariant = "outline" | "solid";

interface ActionButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant: ActionButtonVariant;
}

const ActionButton = memo(({
  variant,
  className = "",
  children,
  ...props
}: ActionButtonProps) => {
  const baseClasses =
    "px-4 sm:px-8 py-2 lg:py-3 w-1/2 xxl:w-auto border border-black transition-colors text-sm font-medium rounded flex items-center justify-center";
  const variantClasses =
    variant === "outline"
      ? "bg-[#14141403] border-[#141414B2] hover:bg-gray-100"
      : "bg-black text-white hover:bg-gray-800";

  return (
    <button
      className={`${baseClasses} ${variantClasses} ${className}`.trim()}
      {...props}
    >
      {children}
      {variant === "solid" && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          className="ml-1"
        >
          <path
            d="M8.28272 3.7356C8.11403 3.51753 8.15394 3.20359 8.37158 3.03442C8.58985 2.86527 8.90457 2.90502 9.07373 3.12329L15.7163 11.6946C15.8559 11.8748 15.8558 12.1266 15.7163 12.3069L9.07373 20.8792C8.90449 21.097 8.5897 21.1371 8.37158 20.968C8.15354 20.7989 8.11391 20.4841 8.28271 20.2659L14.689 12.0012L8.28272 3.7356Z"
            fill="white"
          />
        </svg>
      )}
    </button>
  );
});

ActionButton.displayName = "ActionButton";
